import React, { useEffect, useState } from 'react';
import useSortByLoc from '../hooks/useSortByLoc';
import {
  Filters,
  FiltersMobile,
  SearchFilter,
  PerPage,
  SortBy,
  FeaturedHouse,
  LoadingSpinner,
} from '.';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWpHouse, fetchNotionHouse } from '../store/thunks/house.thunks';
import {
  changeLocation,
  reset,
  setDataBySearch,
  setSortBy,
} from '../store/slices/shortTermHousingRentals.slice';
import { HouseGrid, HouseList } from '.';
import { noResultsMessage, endOfListMessage } from '../contants/data';
import Cookies from 'js-cookie';

// ICONS
import { CiGrid41, CiGrid2H } from 'react-icons/ci';

const ShortTermHousingRentals = () => {
  const category = 28919;
  const otherApiCategory = 'housing-short-term';
  const [gridLayout, setGridLayout] = useState(true);
  const [init, setInit] = useState(false);
  let {
    data: house,
    originalData,
    sorted_data,
    originalSorted_data,
    isLoading,
    searchTerm,
    sortBy,
  } = useSelector((state) => state.shortTermHousingRentals);
  const HouseComponent = gridLayout ? HouseGrid : HouseList;
  const filterOptions = [
    'Price',
    'Beds',
    'Sleeps',
    'Baths',
    'Size_sqft',
    'Unit Type',
    'Rental Property Features',
  ];

  // Cookie
  const cookieExpiryDays = 7; // Cookie will expire in 7 days
  const dispatch = useDispatch();

  // API PAGES
  const mcTotalPages = 5;
  let wpTotalPages = 1;
  let pageKeyWp = 1;
  let pageKeyMc = 1;

  // GEOLOCATE
  const {
    lat,
    lng,
    radius: initialRadus,
  } = useSelector((state) => state.shortTermHousingRentals.filters.location);

  // LOAD MORE
  const [perPage, setPerPage] = useState(12);
  const houseDataFromHook = useSortByLoc(house, lat, lng);

  // Determine the appropriate houseData based on the conditions
  let houseData;

  if (sorted_data.length > 0) {
    houseData = sorted_data.slice(0, perPage);
  } else {
    if (sortBy === 'Closest to Me') {
      houseData = houseDataFromHook.slice(0, perPage);
    } else {
      houseData = [];
    }
  }

  // PRICE
  const { min: priceMin, max: priceMax } = useSelector(
    (state) => state.shortTermHousingRentals.filters.price_range
  );

  // BEDS
  const { min: bedsMin, max: bedsMax } = useSelector(
    (state) => state.shortTermHousingRentals.filters.beds_range
  );

  // SLEEPS
  const { min: sleepsMin, max: sleepsMax } = useSelector(
    (state) => state.shortTermHousingRentals.filters.sleeps_range
  );

  // BATHS
  const { min: bathsMin, max: bathsMax } = useSelector(
    (state) => state.shortTermHousingRentals.filters.beds_range
  );

  // SIZE SQFT
  const { min: sizeSqFtMin, max: sizeSqFtMax } = useSelector(
    (state) => state.shortTermHousingRentals.filters.size_sqft_range
  );

  // UNIT TYPE
  const { unitType: initUnitType } = useSelector(
    (state) => state.shortTermHousingRentals.filters
  );

  // RENTAL PROPERTY FEATURES
  const { rentalPropertyFeatures: initRentalPropertyFeatures } = useSelector(
    (state) => state.shortTermHousingRentals.filters
  );

  const loadMore = () => {
    setPerPage(perPage + perPage);
  };

  const perPageUpdate = (pageNum) => {
    setPerPage((prevState) => (prevState = pageNum.value));
  };

  const handleSortBy = (sortedBy) => {
    dispatch(setSortBy(sortedBy.value));
  };

  const filter = (params) => {
    const {
      finalLat,
      finalLng,
      radius,
      priceVal,
      bedsVal,
      sleepsVal,
      bathsVal,
      sizeSqFtVal,
      unitType,
      rentalPropertyFeatures,
    } = params;
    dispatch(reset());
    fetchData(
      finalLat,
      finalLng,
      radius,
      priceVal,
      bedsVal,
      sleepsVal,
      bathsVal,
      sizeSqFtVal,
      unitType,
      rentalPropertyFeatures
    );

    Cookies.set('location', JSON.stringify({ lat: finalLat, lng: finalLng }), {
      expires: cookieExpiryDays,
    });
  };

  const fetchData = (
    lat,
    lng,
    radius = initialRadus,
    priceVal = [priceMin, priceMax],
    bedsVal = [bedsMin, bedsMax],
    sleepsVal = [sleepsMin, sleepsMax],
    bathsVal = [bathsMin, bathsMax],
    sizeSqFtVal = [sizeSqFtMin, sizeSqFtMax],
    unitType = initUnitType,
    rentalPropertyFeatures = initRentalPropertyFeatures
  ) => {
    dispatch(fetchWpHouse({ pageKeyWp, category }))
      .unwrap()
      .then((response) => {
        wpTotalPages = response.headers['x-wp-totalpages'];
        // ITERATE WP API
        if (pageKeyWp < wpTotalPages) {
          pageKeyWp++;
          fetchData(
            lat,
            lng,
            radius,
            priceVal,
            bedsVal,
            sleepsVal,
            bathsVal,
            sizeSqFtVal,
            unitType,
            rentalPropertyFeatures
          );
        } else {
          const callRealtyApi = () => {
            let params = {
              lat,
              lng,
              radius,
              priceVal,
              bedsVal,
              sleepsVal,
              bathsVal,
              sizeSqFtVal,
              unitType,
              rentalPropertyFeatures,
              otherApiCategory,
            };
            dispatch(fetchNotionHouse(params));
          };

          callRealtyApi();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const searchFilter = () => {
    const filteredData = originalData.filter((item) => {
      const title = item.title?.rendered || item?.lenderName || '';
      return title.toLowerCase().includes(searchTerm.toLowerCase());
    });

    dispatch(setDataBySearch(filteredData));
  };

  useEffect(() => {
    dispatch(reset());
    const locationCookie = Cookies.get('location');

    const handleLocation = (lat, lng) => {
      dispatch(changeLocation({ lat, lng }));
      fetchData(lat, lng);
      setInit(true);
    };

    if (locationCookie) {
      const parsedCookie = JSON.parse(locationCookie);
      handleLocation(parsedCookie.lat, parsedCookie.lng);
    } else {
      const getLocation = () => {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              handleLocation(latitude, longitude);
            },
            () => {
              console.log('Location access denied or error occurred.');
              handleLocation(lat, lng); // Fallback to default coordinates
            }
          );
        } else {
          console.log('Geolocation is not supported in this browser.');
          handleLocation(lat, lng); // Fallback to default coordinates
        }
      };
      getLocation();
    }
  }, [dispatch]);

  return (
    <div className='main-wrapper'>
      <Filters
        onFilter={filter}
        data={house}
        stateCategory='shortTermHousingRentals'
        filters={filterOptions}
      />
      <FiltersMobile
        onFilter={filter}
        data={house}
        stateCategory='shortTermHousingRentals'
        filters={filterOptions}
      />
      <div className='content'>
        {isLoading ? (
          <LoadingSpinner
            text='Please wait...'
            height='80'
            width='80'
            strokeWidth='4'
            animationDuration='1.0'
          />
        ) : (
          <div>
            <div className='top-action-wrap'>
              <div className='left-panel'>
                <span className='results-tag hide-mobile'>
                  {house.length} Results
                </span>
                <div className='layout-buttons'>
                  <div>
                    <span>SORT BY:</span>
                    <SortBy onSortBy={handleSortBy} />
                  </div>
                  <div>
                    <span>PER PAGE:</span>
                    <PerPage onChangePage={perPageUpdate} />
                  </div>
                </div>
              </div>
              <div className='right-panel hide-mobile'>
                <SearchFilter
                  onSearch={searchFilter}
                  stateCategory='shortTermHousingRentals'
                />
                <div className='layout-buttons'>
                  <span>VIEW:</span>
                  <button
                    onClick={() => setGridLayout(true)}
                    className={`${gridLayout ? 'active' : ''}`}
                  >
                    <CiGrid41 size={20} />
                  </button>
                  <button
                    onClick={() => setGridLayout(false)}
                    className={`${!gridLayout ? 'active' : ''}`}
                  >
                    <CiGrid2H size={20} />
                  </button>
                </div>
              </div>
            </div>
            <FeaturedHouse data={house} />
            <HouseComponent data={houseData} type={otherApiCategory} />
            {(() => {
              switch (sortBy) {
                case 'Closest to Me':
                  if (houseData.length < house.length) {
                    return (
                      <button onClick={loadMore} className='primary'>
                        {isLoading ? 'Loading...' : 'Load More'}
                      </button>
                    );
                  } else {
                    if (init) {
                      return (
                        <p className={`${init ? 'end-result-tag' : 'd-none'}`}>
                          {houseData && house.length
                            ? endOfListMessage
                            : noResultsMessage}
                        </p>
                      );
                    }
                  }
                default:
                  if (houseData.length < originalSorted_data.length) {
                    switch (true) {
                      case sorted_data.length === 0:
                        return (
                          <p
                            className={`${init ? 'end-result-tag' : 'd-none'}`}
                          >
                            {noResultsMessage}
                          </p>
                        );
                      case houseData.length < sorted_data.length:
                        return (
                          <button onClick={loadMore} className='primary'>
                            {isLoading ? 'Loading...' : 'Load More'}
                          </button>
                        );
                      default:
                        return (
                          <p
                            className={`${init ? 'end-result-tag' : 'd-none'}`}
                          >
                            {endOfListMessage}
                          </p>
                        );
                    }
                  } else {
                    return (
                      <p className={`${init ? 'end-result-tag' : 'd-none'}`}>
                        {houseData && sorted_data.length
                          ? endOfListMessage
                          : noResultsMessage}
                      </p>
                    );
                  }
              }
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShortTermHousingRentals;
